import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { useRouter } from "next/router";

import { analytics } from "@flare/analytics";
import { useSession } from "@flare/next-auth/react";

import { jsonRequest } from "@/lib/network/json-request";
import type { GeoLocationResponse } from "@/pages/api/geo";

import { useAppContext } from "../store";
import { getValidCurrentUrlForReferrer } from "../store/campaign-data-context/campaign-data-utils";

import type { PageMetaData, ServicesContainerProps } from "./analytics.types";
import { BiEventsNames } from "./bi-event-names";
import { checkIsTestUser } from "./helpers";

export const AppInitService = ({ pageProps }: ServicesContainerProps) => {
  const session = useSession();
  const router = useRouter();
  const {
    campaignData: { extractCampaignData, setCampaignData, campaignData },
  } = useAppContext();

  /**
   * The data team would like to know if the user is logged in or not.
   * This middleware will add a property to all events that will indicate if the user is logged in or not.
   */
  useEffect(() => {
    let isLoggedIn = null;

    if (session?.data?.provider) {
      isLoggedIn = session?.data?.provider !== "anonymous";
    }

    analytics.addSourceMiddleware(({ payload, next }) => {
      if (payload.obj.properties) {
        payload.obj.properties.is_logged_in = isLoggedIn;
      }

      next(payload);
    });
  }, [session.data?.provider]);

  useEffect(() => {
    const campaignData = extractCampaignData();
    const referrer = getValidCurrentUrlForReferrer();
    setCampaignData?.({ ...campaignData, referrer });

    analytics.setPersistentData({
      utm_source: campaignData.source,
      utm_medium: campaignData.medium,
      utm_campaign: campaignData.campaignId,
      utm_sub_campaign: campaignData.adgroupId,
      utm_keyword: campaignData.keyword,
      utm_content: campaignData.content,
      url_referrer: referrer,
      search_network: campaignData.searchNetwork,
      affiliate_id: campaignData.affiliateId,
      gclid: campaignData.gclid,
      fbclid: campaignData.fbclid,
      ttclid: campaignData.ttclid,
      msclkid: campaignData.msclkid,
      match_type: campaignData.matchType,
      match_location: campaignData.matchLocation,
      session_id: crypto.randomUUID?.(),
      screen_width: window.innerWidth,
      screen_height: window.innerHeight,
      brand: "Marble",
      mcidtype: campaignData.mcidtype,
      bu: campaignData.bu,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    analytics.track(BiEventsNames.WebOpenSiteCross);

    // Fire PageView event on first render
    analytics.track(BiEventsNames.WebPageView, {
      pathname: router.asPath,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function handleStartRouteChange() {
      const referrer = getValidCurrentUrlForReferrer();
      setCampaignData?.({ ...campaignData, referrer });

      analytics.setPersistentData({
        url_referrer: referrer,
      });
    }

    function triggerWebPageViewEvent() {
      analytics.page(); // updates context.page values before call any analytics.track
      analytics.track(BiEventsNames.WebPageView, {
        pathname: router.asPath,
      });
    }

    router.events.on("routeChangeComplete", triggerWebPageViewEvent);
    router.events.on("routeChangeStart", handleStartRouteChange);

    return () => {
      router.events.off("routeChangeComplete", triggerWebPageViewEvent);
      router.events.off("routeChangeStart", handleStartRouteChange);
    };
  }, [router, campaignData, setCampaignData]);

  useEffect(() => {
    const metaData = pageProps?.metaData as PageMetaData;

    if (!metaData || !metaData.pageType || !metaData.pageName) {
      return;
    }

    analytics.setPersistentData({
      page_type: metaData.pageType,
      page_name: metaData.pageName,
    });
  }, [pageProps.metaData]);

  const { data: geoLocationData, error: geoLocationError } = useQuery({
    queryKey: ["geo-location"],
    queryFn: () => jsonRequest<GeoLocationResponse>("/api/geo"),
  });

  useEffect(() => {
    if (geoLocationData) {
      analytics.setPersistentData({
        ip_city: geoLocationData.city,
        ip_state: geoLocationData.countryRegion,
        ip_country_code: geoLocationData.country,
        ip_latitude: geoLocationData.latitude,
        ip_longitude: geoLocationData.longitude,
        ip_timezone: geoLocationData.timezone,
        ip_postal_code: geoLocationData.postalCode,
        ip_region: geoLocationData.region,
        ip_continent: geoLocationData.continent,
      });
    }

    if (geoLocationError) {
      analytics.track(BiEventsNames.WebPageError, {
        error_type: "geo-location-error",
        error_message: geoLocationError.message,
      });
    }
  }, [geoLocationData, geoLocationError]);

  useEffect(() => {
    if (
      !session?.data?.user ||
      session.data?.provider === "anonymous" ||
      !session.data.user.marbleId
    ) {
      return;
    }
    analytics.identify(session.data.user.marbleId, {
      userType: checkIsTestUser(session.data.user?.funnelData?.email),
    });
  }, [session.data?.provider, session.data?.user]);

  return null;
};
